import { useEffect } from 'react'
import { classes, stylesheet, style } from 'typestyle'
import { useParams } from 'react-router-dom'

/* Common ======================================================================================= */
import { GLOBAL_CONTENTS } from '../../../common/global-contents'

/* Hooks ======================================================================================== */
import useLocal from '../../../hooks/use-local'
import useFetch from '../../../hooks/use-fetch'
import { useTheme } from '../../../components/theme'

/* Components =================================================================================== */
import { ActionItem } from './components/action-item'
import { Box } from '../../../components/box/box.component'
import { Button } from '../../../components/button'
import { Heading } from '../../../components/text/heading.component'
import { Line } from '../../../components/line'
import { Loading } from '../../../components/loading'
import { Modal } from '../../../components/modal'
import { Notification as TNotification, TablePayload } from '../../../types'
import { Notification } from './components/notification'
import { Sidebar } from '../../../components/sidebar'
import { Tab, Tabs } from '../../../components/tab'
import { Text } from '../../../components/text/text.component'
import '../../modals/notification'

/* Styles ======================================================================================= */
import { paddingLeftInner } from '../../../styles/default/padding'
import { pb12i } from '../../../styles/styleset/padding/pb12i'
import { pl16 } from '../../../styles/styleset/padding/pl16'
import { pr16 } from '../../../styles/styleset/padding/pr16'
import { pt12i } from '../../../styles/styleset/padding/pt12i'
import { t0 } from '../../../styles/styleset/top/t0'
import { z10 } from '../../../styles/styleset/z-index/z10'

Sidebar.add('Notifications', (props: any) => {
  const { unreadCount, $unreadCount } = props
  const params = useParams()
  const [local] = useLocal()

  const url = `${process.env.REACT_APP_API_URL}/a/groups/${params.groupId}/base/notifications/table`
  const [actionItems, $actionItems] = useFetch<TablePayload<TNotification>>(undefined, {
    url,
    search: {
      search: {
        'body.actionRequired': true,
      },
      sort: { key: 'createdAt', direction: -1 },
    },
  })

  const [notifications, $notifications] = useFetch<TablePayload<TNotification>>(undefined, {
    url,
    search: {
      search: {
        'body.actionRequired': false,
      },
      sort: { key: 'createdAt', direction: -1 },
    },
  })

  const [theme] = useTheme()

  useEffect(() => {
    if (actionItems.mounted) {
      $actionItems.get()
    }
  }, [actionItems.mounted])

  useEffect(() => {
    if (notifications.mounted) {
      $notifications.get()
    }
  }, [notifications.mounted])

  const updateItem = (item: TNotification) => {
    $actionItems.$payload.set((prev: any) => {
      for (const data of prev.data) {
        if (data.id === item.id) {
          data.body.read = item.body.read
        }
      }

      return { ...prev }
    })

    $unreadCount.$payload.set((prev: any) => {
      // decrement the total when read otherwise increment
      prev.actionItems += item.body.read ? -1 : 1

      return { ...prev }
    })
  }

  const updateNotification = (item: TNotification) => {
    $notifications.$payload.set((prev: any) => {
      for (const data of prev.data) {
        if (data.id === item.id) {
          data.body.read = item.body.read
        }
      }

      return { ...prev }
    })

    $unreadCount.$payload.set((prev: any) => {
      // decrement the total when read otherwise increment
      prev.notifications += item.body.read ? -1 : 1

      return { ...prev }
    })
  }

  const handleView = (actionItem: any) => {
    Modal.push({
      type: 'Notification',
      componentProps: {
        item: actionItem,
        onComplete(item: any) {
          updateItem(item)
        },
      },
    })
  }

  return (
    <Box fullWidth>
      <Box row>
        <Box className={classes(STYLES.containerHeader, z10, t0)} sticky fullWidth row>
          <Box className={paddingLeftInner} minHeight={52} flex spaceBetween alignCenterY>
            <Box row>
              <Heading className={STYLES.textHeading}>Notifications</Heading>
            </Box>
            <Box flex alignCenterY>
              <Button
                label={GLOBAL_CONTENTS.close[local]}
                baseline={'background'}
                size="sm"
                onClick={() => {
                  Sidebar.pop()
                }}
                square
              />
            </Box>
          </Box>
        </Box>
        <Line />
        <Tabs>
          <Tab
            label={
              <>
                {unreadCount.payload?.actionItems ? (
                  <div
                    className={classes(
                      style({
                        position: 'absolute',
                        top: '-6px',
                        right: '-16px',
                        background: '#ff55b1',
                        color: theme.baselines.error.color.medium,
                        fontSize: '10px',
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }),
                    )}
                  >
                    {unreadCount.payload?.actionItems}
                  </div>
                ) : (
                  ''
                )}
                Action Items
              </>
            }
          >
            <Box baseline="surface" relative>
              {actionItems.meta.isLoading ? (
                <Box className={classes(pb12i, pt12i, pl16, pr16)}>
                  <Loading />
                </Box>
              ) : actionItems.payload?.data?.length ? (
                actionItems.payload?.data?.map(actionItem => (
                  <ActionItem key={actionItem.id} actionItem={actionItem} onUpdate={updateItem} />
                ))
              ) : (
                <Box baseline="none" row>
                  <Line />
                  <Box
                    className={classes(pb12i, pt12i, style({ cursor: 'pointer' }), pl16, pr16)}
                    baseline="none"
                    flex
                    fullWidth
                    alignCenterY
                  >
                    <Text>You do not have any action items assigned to you.</Text>
                  </Box>
                </Box>
              )}
            </Box>
          </Tab>
          <Tab
            label={
              <>
                {unreadCount.payload?.notifications ? (
                  <div
                    className={classes(
                      style({
                        position: 'absolute',
                        top: '0px',
                        left: '12px',
                        background: '#ff55b1',
                        color: theme.baselines.error.color.medium,
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }),
                    )}
                  ></div>
                ) : (
                  ''
                )}
                Notifications
              </>
            }
          >
            <Box baseline="surface" relative>
              {notifications.meta.isLoading ? (
                <Box className={classes(pb12i, pt12i, pl16, pr16)}>
                  <Loading />
                </Box>
              ) : notifications.payload?.data?.length ? (
                notifications.payload?.data?.map(notification => (
                  <Notification
                    key={notification.id}
                    notification={notification}
                    onUpdate={updateNotification}
                  />
                ))
              ) : (
                <Box baseline="none" row>
                  <Line />
                  <Box
                    className={classes(pb12i, pt12i, style({ cursor: 'pointer' }), pl16, pr16)}
                    baseline="none"
                    flex
                    fullWidth
                    alignCenterY
                  >
                    <Text>You do not have any notifications.</Text>
                  </Box>
                </Box>
              )}
            </Box>
          </Tab>
        </Tabs>
      </Box>
    </Box>
  )
})

const STYLES = stylesheet({
  containerHeader: {
    height: 53,
  },

  textHeading: {
    maxWidth: 368,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },

  divider: {
    maxHeight: 30,
    minHeight: 30,
  },
})
